import { Paper, TableBody, TableContainer, TableHead, TableRow, Theme, useMediaQuery } from "@mui/material";
import TextCell from "../components/TextCell";
import { useState } from "react";
import SwitchCell from "../components/SwitchCell";
import { ISliderInterface } from "../components/SliderCell";
import { DefaultButton, SettingTable, TableCellButton, settingUseStyles } from "../Settings";
import SliderRow from "../components/SliderRow";
import { useSnackbar } from "notistack";
import ConfirmationDialog from "../../../components/Misc/Dialog/ConfirmationDialog";

interface IICAO {
    findDefaultFVSliderInterface: (name: string) => ISliderInterface | undefined,
    findFVSliderInterface: (name: string) => (ISliderInterface | undefined),
    setFVSliderInterfaceOption: (op: ISliderInterface, basic?: boolean | undefined) => void
    resetValueToDefault: (name: string) => void,
    setOption: (key: string, value: string | number | boolean, basic?: boolean) => void
    icaoCompliance: boolean
    backgroundRemoval: boolean
}

const ICAO: React.FC<IICAO> = (props) => {

    const classes = settingUseStyles()
    const [active, setActive] = useState<boolean>(props.icaoCompliance);

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [openDialog, setOpenDialog] = useState<boolean>(false)
    const { enqueueSnackbar } = useSnackbar();

    const setIcao = (icao: boolean) => {
        setActive(icao)
        props.setOption("checkIcaoCompliance", icao, true)
    }

    const handleResetClick = () => {
        enqueueSnackbar("ICAO settings reset", {variant: "info"})
        props.resetValueToDefault("IcaoBackgroundUniformityThreshold")
        props.resetValueToDefault("IcaoLookingAwayThreshold")
        props.resetValueToDefault("IcaoRedEyeThreshold")
        props.resetValueToDefault("IcaoFaceDarknessThreshold")
        props.resetValueToDefault("IcaoUnnaturalSkinToneThreshold")
        props.resetValueToDefault("IcaoWashedOutThreshold")
        props.resetValueToDefault("IcaoPixelationThreshold")
        props.resetValueToDefault("IcaoSkinReflectionThreshold")
        props.resetValueToDefault("IcaoGlassesReflectionThreshold")
    }

    return(
        <>
        <TableContainer component={Paper} className={classes.container}>
            <SettingTable>
                <TableHead>
                    <TableRow>
                        <TextCell width="50%" head title="ICAO" subtitle=""/>
                        <TableCellButton align="right" colSpan={isMobile? 1 : 2}>
                            <DefaultButton variant="text" color="primary" onClick={() => setOpenDialog(true)}>
                                Reset to default
                            </DefaultButton>
                        </TableCellButton>
                        <SwitchCell state={active} setState={setIcao}/>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Background uniformity"
                        subtitle="Determines the consistency and homogeneity of the background in an image or video frame."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoBackgroundUniformityThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoBackgroundUniformityThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="raising"
                        active={active}
                        lockMaxValue
                        info="Produces a value on [0, 100]. A score of 0 indicates a highly varied or cluttered background with multiple colors, patterns, or objects, whereas a score of 100 signifies a completely uniform background with a single color or shade."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Looking away"
                        subtitle="Determines the degree to which an individual's gaze deviates from directly facing the camera."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoLookingAwayThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoLookingAwayThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active={active}
                        lockMaxValue
                        info="Produces value on [0, 100]. A score of 0 suggests that the person is looking straight at the camera, whereas a score of 100 indicates a complete diversion from the camera's direction. Higher values imply that the person is more likely looking away."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Red eye"
                        subtitle="Determines the presence and intensity of redness in an individual's eyes."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoRedEyeThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoRedEyeThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active={active}
                        lockMaxValue
                        info="Produces value on [0, 100]. A score of 0 indicates no detectable redness in the eyes, while a score of 100 signifies pronounced red eyes. Higher values are indicative of greater redness"
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Face darkness"
                        subtitle="Determines the underexposure level of an individual's face within an image."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoFaceDarknessThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoFaceDarknessThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active={active}
                        lockMaxValue
                        info="Produces value on [0, 100]. A score of 0 suggests that the face is well-lit and appropriately exposed, while a score of 100 indicates severe underexposure, resulting in a darkened facial appearance"
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Unnatural skin tone"
                        subtitle="Determines the naturalness of an individual's skin tone in an image."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoUnnaturalSkinToneThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoUnnaturalSkinToneThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="raising"
                        active={active}
                        lockMaxValue
                        info="Produces value on [0, 100]. A score closer to 0 suggests that the skin tone appears altered or unnatural, while a score closer to 100 indicates that the skin tone appears authentic and true to life. Higher values are indicative of a more natural-looking skin tone."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Washed out"
                        subtitle="Determines the sharpness and vibrancy of colors in an image."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoWashedOutThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoWashedOutThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active={active}
                        lockMaxValue
                        info='Produces value on [0, 100]. A score of 0 indicates that the colors are vibrant and well-defined, while a score of 100 signifies that the colors appear faded or muted, lacking in depth and richness. Higher scores indicate that the colors in the image are more "washed out" or desaturated'
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Pixelation"
                        subtitle="Determines the extent of pixelation or the visibility of individual pixels in an image."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoPixelationThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoPixelationThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active={active}
                        lockMaxValue
                        info="Produces value on [0, 100]. A score of 0 suggests a high-resolution, smooth image without noticeable pixel boundaries, whereas a score of 100 denotes severe pixelation where individual pixels are clearly discernible, often leading to a blocky or grainy appearance. Higher values represent increased levels of pixelation."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Skin reflection"
                        subtitle="Determines the presence and intensity of light reflections on an individual's face within an image."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoSkinReflectionThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoSkinReflectionThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active={active}
                        lockMaxValue
                        info="Produces value on [0, 100]. A score of 0 suggests the absence of noticeable reflections, whereas a score of 100 indicates significant light reflections on the face. Higher values signify an increasing presence of reflections."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Glasses reflection"
                        subtitle="Determines whether glasses are present or not."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoGlassesReflectionThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoGlassesReflectionThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active={active}
                        lockMaxValue
                        info="Produces value on [0, 100]. A score of 0 indicates no discernible reflection on the glasses, while a score of 100 signifies pronounced light reflection on the glasses. Higher values represent an increased presence of reflections"
                    />
                </TableBody>
            </SettingTable>
        </TableContainer>
        <ConfirmationDialog
            open={openDialog}
            setOpen={setOpenDialog}
            title="Reset ICAO settings"
            text="Reset ICAO settings to default values?"
            cancelText="Cancel"
            confirmText="Yes"
            confirm={handleResetClick}
            confirmColor="error"
        />
        </>
    );
}

export default ICAO;